import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Checkbox, MultiSelect, NativeSelect, NumberInput, Radio, TextInput, Textarea } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { CandidateProperty, CriteriaSet } from "../api/types";
import type { SignedIn } from "../lib/auth";
import {
  LIST_UNKNOWN, MEASURES, PRESETS, RANKINGS, YEAR_NOTE, YEAR_OF, applyPreview, criterion, forSaving, isPreviewable,
  isSet, listHasKnown, notPreviewable, NOT_COUNTED, presetIsOn, presetSays, rangeSet, rankingFor, rankingOf,
  withBound, withKeep, withNoValue, withPreset, yearOf,
  type Filters, type Measure, type RankKey, type RankState, type Weights,
} from "../lib/campaignFilters";
import { CriteriaEditor } from "../components/CriteriaEditor";
import { UnknownBox } from "../components/UnknownBox";
import { callNotesFrom } from "../components/CallNotesFields";
import { EnrichmentOptionsFields, NEW_CAMPAIGN_OPTIONS, optionsArgs, optionsFrom, type EnrichmentOptions } from "../components/EnrichmentOptions";
import { ListShell } from "../components/ListShell";
import { Spinner } from "../components/Spinner";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { absent, orAbsent } from "../lib/format";
import { campaignSlug, slugProblem } from "../lib/slug";
import { WEB_COST, campaignPath, useScheduleSetting, useUserAccounts } from "../lib/campaign";
import { saveLine, suggestedEnrichOn, todayIso, type StartMode } from "../lib/schedule";
import { SequencePicker, aheadOf, useSequenceTargets, type SequenceWhere } from "../components/CampaignSequence";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { IconArrowLeft } from "../ui/icons";
import { useConfirm } from "../ui/Dialog";

/* Making a campaign, editing its criteria, or copying one.
 *
 * Four sections in Nestor's order, Property type, Location, Size and
 * Contacts (plain labels, James, 2026-10-12), with every other criterion in
 * its group under them, then, for a new campaign, the enrichment it starts
 * with, its place in a sequence, its owner, its dates and its description.
 * Nothing folds,
 * because a setting in a fold gets missed (docs/ux-decisions.md). The
 * answer is pinned beside the questions: how many properties match, what
 * each criterion costs (the count without it), the ranking, the name and the
 * button. The matching properties are under both.
 *
 * Call notes are not asked here. They are asked on the campaign's page
 * before the first Orum export, which is when they are used. Making the
 * campaign is one engine call (api.create_campaign, engine migrations 202
 * and 256), which saves it with its list and its dates and starts its
 * enrichment now, on its date, or not yet. One button, Save campaign, with a
 * line beside it that says which (James, 2026-10-19,
 * docs/plans/campaign-scheduling.md in the engine).
 *
 * The Sequence question puts it after or before another campaign in the
 * same call (engine migration 314), so a place the engine refuses makes no
 * campaign. Choosing a campaign makes its owner the owner, and with a
 * campaign ahead, enrichment waits for that one to start calling unless the
 * person says otherwise (James, 2026-11-03).
 *
 * docs/plans/campaigns-overhaul.md, The builder. The count reads
 * api.candidate_property, candidacy's own query, so it is the run's answer
 * for every criterion except the four NOT_COUNTED names, which the screen
 * names rather than folding into a number it cannot stand behind. */
const CSV_DEFAULT = ["name", "address_line1", "city", "state", "property_type", "unit_count",
                     "owner_company_name", "chargers_state", "portfolio_size", "owner_mobiles"] as const;

const ROWS = "id,name,address_line1,city,state,property_type,unit_count,owner_company_id,owner_company_name,chargers_state,standing_stock,has_reachable_person,portfolio_size,owner_mobiles";

/** The criteria the questions ask, left out of the folded groups. The Year
 *  section and its settings since engine migration 275. */
const ASKED = ["state_in", "city_in", "min_units", "max_units", "year_of", "year_min", "year_max",
               "include_unknown"];

/** A bound typed into a number box, or no bound when the box is empty. */
const bound = (v: string | number) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

function propertyLabel(p: { name?: string | null; address_line1?: string | null }) {
  return (p.name ?? "").trim() || (p.address_line1 ?? "").trim() || orAbsent(null);
}

export function CampaignBuilder({ session }: { session: SignedIn }) {
  const token = session.idToken;
  const [search] = useSearchParams();
  const editing = search.get("edit");
  const copying = search.get("copy");
  const source = editing ?? copying;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [nameEdited, setNameEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [filters, setFilters] = useState<Filters>({});
  const [ranking, setRanking] = useState<RankState>("none");
  const [people, setPeople] = useState<EnrichmentOptions>(NEW_CAMPAIGN_OPTIONS);
  const [callNotes, setCallNotes] = useState<unknown>(null);
  // Empty by default: a campaign is usually assigned after it is made and
  // enriched (James, 2026-10-09).
  const [owner, setOwner] = useState("");
  const accounts = useUserAccounts(token);
  // Off by default, so nothing spends Apollo credits unless somebody chose
  // it (James, 2026-10-19).
  const [askApollo, setAskApollo] = useState(false);
  const [callOn, setCallOn] = useState("");
  const [mode, setMode] = useState<StartMode>("now");
  const [enrichOn, setEnrichOn] = useState("");
  // Once the person sets when enrichment starts, a new calling date no
  // longer moves it.
  const [startTouched, setStartTouched] = useState(false);
  const lead = useScheduleSetting(token);
  const [seqWhere, setSeqWhere] = useState<SequenceWhere>("after");
  const [seqTarget, setSeqTarget] = useState("");
  const sequenceTargets = useSequenceTargets(token, !editing);
  const [said, setSaid] = useState<string | null>(null);
  const [saving, setSaving] = useState<null | "create" | "save">(null);
  /* Editing only: Save changes also updates the list, unless this is unticked. */
  const [updateOnSave, setUpdateOnSave] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const confirm = useConfirm();

  const params = useListParams("has_reachable_person", [] as const, false);
  const csvColumns = useCsvColumns("campaign-candidates", "candidate_property", CSV_DEFAULT);

  /* Read fresh every time the builder opens, and kept nowhere after it
     closes. The form is filled from this row and Save changes sends it all
     back, so a cached row from before a save, or from before the Enrichment
     tab turned web enrichment on, was written back over the change until
     2026-10-20. */
  const existing = useQuery({
    queryKey: ["criteria-set", source],
    enabled: Boolean(source),
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("criteria_set").select("*").eq("name", source as string).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as CriteriaSet | null;
    },
  });

  // Debounced, so the count follows typing rather than every keystroke.
  const [settled, setSettled] = useState<Filters>({});
  useEffect(() => {
    const t = setTimeout(() => setSettled(filters), 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    const c = existing.data;
    if (!c) return;
    const f = (c.filters ?? {}) as Filters;
    setFilters(f);
    setSettled(f);
    setRanking(rankingFor(c.weights as Weights | null));
    setDescription(c.description ?? "");
    setPeople({ ...optionsFrom(c), askEnricher: false });
    if (editing) {
      setLabel(c.label ?? c.name ?? "");
      setName(c.name ?? "");
      setNameEdited(true);
    } else {
      const copyLabel = `Copy of ${c.label ?? c.name ?? ""}`;
      setLabel(copyLabel);
      setName(campaignSlug(copyLabel));
      setCallNotes(callNotesFrom(c.shape));
    }
    setLoadedFor(c.name ?? source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  /* Nothing is counted until the source campaign's criteria are in, or the
     whole book is counted one render before the real question, which is the
     most expensive thing this view can be asked (2026-09-15). */
  const ready = !source || loadedFor === source;

  useEffect(() => {
    if (params.page !== 1) params.set({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const narrowed = (opts?: { count?: "exact"; head?: boolean }, columns?: string, f: Filters = settled) =>
    applyPreview(apiClient(token).from("candidate_property").select(columns ?? ROWS, opts), f);

  const rows = useQuery({
    queryKey: ["candidates", settled, params.page, params.sorts],
    enabled: ready,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        .order("id", { ascending: true }).range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CandidateProperty[];
    },
  });

  const count = useQuery({
    queryKey: ["candidates", "count", settled],
    enabled: ready,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { error, count: n } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return n ?? null;
    },
  });

  /* What each criterion costs: the count with that one removed. Asked one
     at a time after the main count lands, so the counts never queue
     together in the API's connection pool. */
  const counted = Object.keys(settled).filter((k) => isPreviewable(k) && isSet(settled[k]));
  const without = useQuery({
    queryKey: ["candidates", "without", settled],
    enabled: ready && count.isSuccess && !count.isPlaceholderData && counted.length > 0,
    queryFn: async () => {
      const out: { key: string; n: number | null }[] = [];
      for (const key of counted) {
        const rest = { ...settled };
        delete rest[key];
        const { error, count: n } = await narrowed({ count: "exact", head: true }, "id", rest);
        if (error) throw new Error(apiErrorMessage(error));
        out.push({ key, n: n ?? null });
      }
      return out;
    },
  });

  /* How many of the properties the other criteria match have no value for
     each range that is set: the number its box adds when ticked. Asked after
     the main count, one range at a time, like the counts above. */
  const ranged = (Object.keys(MEASURES) as Measure[]).filter((m) => rangeSet(settled, m));
  // And each list with a known value ticked whose value can be not known,
  // such as property type (James, 2026-10-22).
  const listed = Object.keys(LIST_UNKNOWN).filter((k) => listHasKnown(settled, k));
  const unknowns = useQuery({
    queryKey: ["candidates", "unknown", settled],
    // Ticking a box recounts, and the label keeps its number meanwhile
    // rather than losing it for the length of the count.
    placeholderData: keepPreviousData,
    enabled: ready && count.isSuccess && !count.isPlaceholderData && ranged.length + listed.length > 0,
    queryFn: async () => {
      const out: Partial<Record<string, number | null>> = {};
      for (const m of ranged) {
        const rest = withKeep({ ...settled }, m, false);
        for (const k of MEASURES[m].keys) delete rest[k];
        const { error, count: n } = await withNoValue(
          narrowed({ count: "exact", head: true }, "id", rest), m, settled);
        if (error) throw new Error(apiErrorMessage(error));
        out[m] = n ?? null;
      }
      for (const k of listed) {
        const u = LIST_UNKNOWN[k];
        if (!u) continue;
        const rest = { ...settled };
        delete rest[k];
        const q = narrowed({ count: "exact", head: true }, "id", rest);
        const { error, count: n } = await (u.isNull ? q.is(u.column, null) : q.eq(u.column, u.value));
        if (error) throw new Error(apiErrorMessage(error));
        out[k] = n ?? null;
      }
      return out;
    },
  });

  const states = useQuery({
    queryKey: ["candidate-state-count"],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("candidate_state_count")
        .select("state,properties").order("properties", { ascending: false }).limit(80);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as { state: string | null; properties: number | null }[];
    },
  });

  /* A new campaign with automated enrichment on starts a web run over what
     matches. The engine says whether a run that size is large enough to ask
     first (api.web_sweep_size, engine migration 207). The count is of
     properties, and the run sends their records with gaps, so the screen
     names the properties and gives no hours. */
  const matched = count.data ?? null;
  const sweep = useQuery({
    queryKey: ["web-sweep-size", matched],
    enabled: !editing && matched !== null,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).rpc("web_sweep_size", { p_records: matched as number }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as { needs_confirm: boolean | null }[])[0] ?? null;
    },
  });
  const bigRun = Boolean(people.autoEnrich && sweep.data?.needs_confirm);

  const uncounted = useMemo(() => notPreviewable(settled), [settled]);
  const nameProblem = slugProblem(name.trim());
  const segment = PRESETS.find((p) => presetIsOn(filters, p));
  const stateList = Array.isArray(filters.state_in) ? (filters.state_in as string[]) : [];
  const setFilter = (key: string, value: Filters[string] | undefined) => {
    const next = { ...filters };
    if (value === undefined || (Array.isArray(value) && !value.length)) delete next[key];
    else next[key] = value;
    setFilters(next);
  };

  const weights = ranking === "engine" ? undefined : rankingOf(ranking as RankKey).weights;

  const today = todayIso();
  const anyEnrichment = people.autoEnrich || people.askEnricher || askApollo;
  /* The campaign chosen in Sequence, and the one that will be ahead of the
     new campaign. Behind one being called, the engine starts the chosen
     enrichment on saving (lake.start_sequence_follower, engine migration
     311), so the form sends Now and says why. */
  const targets = sequenceTargets.data ?? [];
  const placeTarget = targets.find((t) => t.name === seqTarget) ?? null;
  const ahead = aheadOf(targets, seqWhere, seqTarget);
  const aheadLabel = ahead ? ahead.label ?? ahead.name ?? "" : "";
  const startsWithAhead = anyEnrichment && ahead?.stage === "calling";
  const startMode: StartMode = startsWithAhead ? "now" : mode;
  const startsNow = anyEnrichment && (startMode === "now" || (startMode === "on_date" && enrichOn !== "" && enrichOn <= today));
  /* A calling date suggests when enrichment starts: the engine's lead days
     before it, or today when that has passed (James, 2026-10-19), until the
     person sets the start themselves. Not in a sequence with a campaign
     ahead, where enrichment waits for that one to start calling. */
  const suggestStart = (value: string) => {
    if (!value || lead.data === undefined) return;
    const suggested = suggestedEnrichOn(value, lead.data, today);
    if (suggested > today) { setMode("on_date"); setEnrichOn(suggested); }
    else { setMode("now"); setEnrichOn(""); }
  };
  const pickCallOn = (value: string) => {
    setCallOn(value);
    if (!startTouched && !ahead) suggestStart(value);
  };
  /* A place in a sequence takes the owner of the campaign chosen, as a move
     on the campaign page does, and until the person sets when enrichment
     starts, a campaign ahead makes it wait for that one. */
  const placeAt = (where: SequenceWhere, target: string) => {
    setSeqWhere(where); setSeqTarget(target);
    const chosen = targets.find((t) => t.name === target);
    if (chosen) setOwner(chosen.owner_id ?? "");
    if (startTouched) return;
    setMode(aheadOf(targets, where, target) ? "not_yet" : "now"); setEnrichOn("");
    if (!aheadOf(targets, where, target)) suggestStart(callOn);
  };

  const create = async () => {
    setSaving("create"); setSaid(null);
    try {
      await callFunction<string>(token, "create_campaign", {
        p_name: name.trim(), p_label: label.trim(), p_filters: forSaving(filters),
        ...(weights ? { p_weights: weights } : {}),
        p_description: description.trim() || null,
        p_auto_enrich: people.autoEnrich, p_ask_enricher: people.askEnricher,
        p_roles: people.roles, p_seniorities: people.seniorities,
        p_contact_state: people.contactState.trim() || null,
        p_research: { notes: people.researchNotes.trim() },
        ...(callNotes ? { p_call_notes: callNotes } : {}),
        // With a place in a sequence the engine takes that sequence's owner,
        // so the form sends none and cannot send a stale one (migration 314).
        ...(owner && !seqTarget ? { p_owner: owner } : {}),
        ...(people.contactCut !== null ? { p_contact_cut: people.contactCut } : {}),
        ...(people.perRole ? { p_per_role: people.perRole } : {}),
        ...(people.perCompany ? { p_per_company: people.perCompany } : {}),
        p_reach_fill: people.reachFill,
        p_ask_apollo: askApollo,
        // With no enrichment chosen there is nothing to start, and the
        // engine makes the campaign Ready to call.
        p_start_enrichment: anyEnrichment ? startMode : "now",
        ...(anyEnrichment && startMode === "on_date" ? { p_enrich_on: enrichOn } : {}),
        ...(callOn ? { p_call_on: callOn } : {}),
        ...(seqTarget ? (seqWhere === "after" ? { p_after: seqTarget } : { p_before: seqTarget }) : {}),
      });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["sequence-targets"] });
      queryClient.invalidateQueries({ queryKey: ["my-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["enrichment-coming-up"] });
      navigate(campaignPath(name.trim()));
    } catch (e) {
      setSaving(null);
      setSaid(e instanceof Error ? e.message : String(e));
    }
  };

  /* A large web run that starts on saving is confirmed first. One that
     starts on a later date is not, because the line under Web enrichment has
     said what it costs and nothing starts yet. */
  const startCreate = () => {
    if (!bigRun || !startsNow || matched === null) { void create(); return; }
    confirm.ask({
      title: `Start web enrichment on ${matched.toLocaleString()} properties?`,
      says: "Web enrichment is ticked and starts when you save. " + WEB_COST + " To start it later, choose a date under Dates.",
      label: "Save and start web enrichment",
      run: () => create(),
    });
  };

  const saveChanges = async () => {
    setSaving("save"); setSaid(null);
    let saved = false;
    try {
      await callFunction(token, "save_criteria_set", {
        p_name: editing, p_filters: forSaving(filters), p_label: label.trim() || null,
        p_description: description.trim() || null, p_entity_type: "property",
        ...(weights ? { p_weights: weights } : {}),
      });
      /* Who to reach is on this screen, so Save changes saves it. It did not
         until 2026-10-08, and a change to the roles here was dropped without
         a word. The automatic run's switch and the research notes are sent as
         the campaign has them, so this starts nothing. */
      await callFunction(token, "set_enrichment_options", optionsArgs(editing as string, people));
      saved = true;
      /* The same call as Update the list on the campaign page. Ticked by
         default, because each criteria change James and Nestor made by hand
         from 2026-10-19 to 2026-10-26 was followed by a list update, some of
         them hours later (engine docs/plans/list-behind-criteria.md). */
      if (updateOnSave) await callFunction(token, "run_campaign", { p_criteria_set: editing });
      queryClient.invalidateQueries({ queryKey: ["campaign", editing] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(campaignPath(editing as string, updateOnSave ? "list" : "criteria"));
    } catch (e) {
      setSaving(null);
      const why = e instanceof Error ? e.message : String(e);
      setSaid(saved ? `Saved. The list was not updated: ${why}` : why);
    }
  };

  const list = rows.data ?? [];
  const n = count.data ?? null;
  /* The data table, so a phone gets stacked rows and the page never scrolls
     sideways. Names and owners are links, and a row does not open: the
     criteria here are not saved, and leaving would lose them. */
  const columns: ColumnSpec<CandidateProperty>[] = [
    { id: "name", header: "Property", role: "leading", fields: [], sort: "name", title: true, narrowLabel: null,
      cell: (p) => p.id ? <Link to={`/properties/${p.id}`}>{propertyLabel(p)}</Link> : propertyLabel(p) },
    // City and state are two columns, because a person sorts by state and
    // filters by city (docs/design-standards.md).
    { id: "city", header: "City", role: "leading", fields: [], sort: "city", narrowLabel: null, cell: (p) => orAbsent(p.city) },
    { id: "state", header: "State", role: "leading", fields: [], sort: "state", narrowLabel: null, cell: (p) => orAbsent(p.state) },
    { id: "units", header: "Units", role: "secondary", fields: [], align: "num", sort: "unit_count", narrowLabel: "units",
      cell: (p) => p.unit_count?.toLocaleString() ?? absent() },
    { id: "owner", header: "Owner", role: "secondary", fields: [], narrowLabel: null,
      cell: (p) => p.owner_company_id && p.owner_company_name
        ? <Link to={`/companies/${p.owner_company_id}`}>{p.owner_company_name}</Link>
        : orAbsent(p.owner_company_name) },
    { id: "portfolio", header: "Portfolio", role: "secondary", fields: [], align: "num", sort: "portfolio_size", narrowLabel: "portfolio",
      cell: (p) => p.owner_company_id ? (p.portfolio_size?.toLocaleString() ?? absent()) : absent() },
    { id: "chargers", header: "Chargers", role: "detail", fields: [], narrowLabel: "chargers", cell: (p) => orAbsent(p.chargers_state) },
    { id: "reachable", header: "Reachable", role: "secondary", fields: [], narrowLabel: "reachable",
      sort: "has_reachable_person", sortDescendingFirst: true,
      cell: (p) => <span className="muted">{p.has_reachable_person ? "yes" : p.owner_mobiles ? "mobile" : "no"}</span> },
  ];
  const title = editing ? `Edit ${existing.data?.label ?? editing}` : copying ? "Copy a campaign" : "New campaign";
  const canSave = Boolean(label.trim()) && !nameProblem && !saving
    && !(anyEnrichment && startMode === "on_date" && !enrichOn);
  const line = saveLine({
    anyEnrichment, mode: startMode, enrichOn, callOn, today,
    sequence: placeTarget ? {
      where: seqWhere, target: placeTarget.label ?? placeTarget.name ?? "", owner: placeTarget.owner_name ?? null,
      ahead: ahead ? aheadLabel : null, aheadCalling: ahead?.stage === "calling",
    } : null,
  });

  return (
    <ListShell
      title={title}
      params={params}
      csvColumns={csvColumns}
      total={n}
      totalPending={count.isPending}
      rowCount={list.length}
      isLoading={rows.isLoading || !ready}
      isFetching={rows.isFetching}
      isError={rows.isError}
      error={rows.error as Error | null}
      dataUpdatedAt={rows.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["candidates"] })}
      onRetry={() => rows.refetch()}
      onDownload={() => downloadNarrowed({
        name: `campaign-${name.trim() || "draft"}`,
        columns: csvColumns.chosen,
        total: n,
        fetchAll: async (ceiling) => {
          const { data, error } = await applySorts(narrowed(undefined, csvColumns.chosen.join(",")), params.sorts)
            .range(0, ceiling - 1);
          if (error) throw new Error(apiErrorMessage(error));
          return (data ?? []) as unknown as CandidateProperty[];
        },
      })}
      emptyEverything="No properties match these criteria. Loosen one. Without each criterion, beside the count, says which."
      lead={
        <div className="cb">
          <div className="cb-questions">
            {editing || copying ? (
              <p className="cb-back">
                <Link to={campaignPath(source as string)} className="ui-back"><IconArrowLeft />Back to {existing.data?.label ?? source}</Link>
              </p>
            ) : null}

            <section className="cb-q" aria-labelledby="q-kind">
              <h2 id="q-kind">Property type</h2>
              <div className="bar">
                {PRESETS.map((p) => (
                  <Button key={p.label} aria-pressed={presetIsOn(filters, p)} className={presetIsOn(filters, p) ? "pressed" : undefined}
                          onClick={() => {
                            const on = presetIsOn(filters, p);
                            setFilters(withPreset(filters, p));
                            if (!on) setRanking(p.rankBy);
                          }}>
                    {p.label}
                  </Button>
                ))}
              </div>
              {segment ? <p className="hint">{presetSays(segment)}</p> : null}
            </section>

            <section className="cb-q" aria-labelledby="q-where">
              <h2 id="q-where">Location</h2>
              <MultiSelect
                label="State" searchable clearable
                value={stateList}
                data={(states.data ?? []).filter((s) => s.state).map((s) => ({ value: s.state as string, label: s.state as string }))}
                renderOption={({ option }) => (
                  <span className="cb-state">
                    <span>{option.value}</span>
                    <span className="muted">{(states.data?.find((s) => s.state === option.value)?.properties ?? 0).toLocaleString()}</span>
                  </span>
                )}
                description={states.isLoading ? "Counting the properties in each state." : "The number is how many properties EVIE has there."}
                onChange={(v) => setFilter("state_in", v)}
                maw="34rem"
              />
              <TextInput
                label="City" description="Comma separated." maw="34rem" mt="sm"
                defaultValue={Array.isArray(filters.city_in) ? (filters.city_in as string[]).join(", ") : ""}
                key={`city-${loadedFor ?? ""}`}
                onBlur={(e) => setFilter("city_in", e.currentTarget.value.split(",").map((s) => s.trim()).filter(Boolean))}
              />
            </section>

            <section className="cb-q" aria-labelledby="q-size">
              <h2 id="q-size">Size</h2>
              <div className="cb-range">
                <NumberInput
                  label="At least" suffix={filters.min_units === 1 ? " unit" : " units"} min={0}
                  value={typeof filters.min_units === "number" ? filters.min_units : ""}
                  onChange={(v) => setFilters(withBound(filters, "min_units", bound(v)))}
                />
                <NumberInput
                  label="At most" suffix={filters.max_units === 1 ? " unit" : " units"} min={0}
                  value={typeof filters.max_units === "number" ? filters.max_units : ""}
                  onChange={(v) => setFilters(withBound(filters, "max_units", bound(v)))}
                />
              </div>
              <UnknownBox filters={filters} measure="units" count={unknowns.data?.units}
                          failed={unknowns.isError} onChange={setFilters} />
            </section>

            {/* A range of years on the year Year of names, every year in it
                ranked the same and no year ranked lower (James, 2026-10-20,
                engine migration 275). */}
            <section className="cb-q" aria-labelledby="q-year">
              <h2 id="q-year">Year</h2>
              <div className="cb-range">
                <NativeSelect
                  className="cb-range-wide" label="Year of" value={yearOf(filters.year_of).value}
                  data={YEAR_OF.map((y) => ({ value: y.value, label: y.label }))}
                  onChange={(e) => setFilters({ ...filters, year_of: e.currentTarget.value })}
                />
                <NumberInput
                  label="From" min={1800} max={2100} allowDecimal={false}
                  value={typeof filters.year_min === "number" ? filters.year_min : ""}
                  onChange={(v) => setFilters(withBound(filters, "year_min", bound(v)))}
                />
                <NumberInput
                  label="To" min={1800} max={2100} allowDecimal={false}
                  value={typeof filters.year_max === "number" ? filters.year_max : ""}
                  onChange={(v) => setFilters(withBound(filters, "year_max", bound(v)))}
                />
              </div>
              {"help" in yearOf(filters.year_of) ? (
                <p className="hint">{(yearOf(filters.year_of) as { help: string }).help}</p>
              ) : null}
              <UnknownBox filters={filters} measure="year" count={unknowns.data?.year}
                          failed={unknowns.isError} onChange={setFilters} />
              <p className="hint">{YEAR_NOTE}</p>
            </section>

            <section className="cb-q" aria-labelledby="q-who">
              <h2 id="q-who">Contacts</h2>
              <EnrichmentOptionsFields token={token} value={people} onChange={setPeople}
                                       campaign={editing ?? (name.trim() || null)} />
            </section>

            <section className="cb-q" aria-labelledby="q-more">
              <h2 id="q-more">More criteria</h2>
              <CriteriaEditor token={token} filters={filters} onChange={setFilters}
                              ranking={ranking} onRanking={setRanking} groupsOnly omit={ASKED}
                              unknownCounts={unknowns.data} />
            </section>

            {!editing ? (
              <section className="cb-q" aria-labelledby="q-enrich">
                <h2 id="q-enrich">Enrichment</h2>
                <Checkbox checked={people.autoEnrich} onChange={(e) => setPeople({ ...people, autoEnrich: e.currentTarget.checked })}
                          label="Web enrichment"
                          description="EVIE searches the web for these properties' owners and contacts." />
                {bigRun && matched !== null ? (
                  <p className="hint cb-warn">
                    That is web enrichment on {matched.toLocaleString()} properties. {WEB_COST}
                  </p>
                ) : null}
                <Checkbox mt="sm" checked={askApollo} onChange={(e) => setAskApollo(e.currentTarget.checked)}
                          label="Apollo enrichment"
                          description="Asks Apollo about the people to call who have no mobile." />
                <Checkbox mt="sm" checked={people.askEnricher} onChange={(e) => setPeople({ ...people, askEnricher: e.currentTarget.checked })}
                          label="Manual enrichment"
                          description="A researcher finds what EVIE cannot, in CoStar and ZoomInfo." />
                {people.askEnricher ? (
                  <Textarea mt="sm" ml="xl" maw="32rem" autosize minRows={2} label="Research notes"
                            description="For whoever pulls from CoStar, ZoomInfo or other sources: submarkets, secondary types, how to split a pull. Not a filter."
                            value={people.researchNotes} onChange={(e) => setPeople({ ...people, researchNotes: e.currentTarget.value })} />
                ) : null}
              </section>
            ) : null}

            {!editing ? (
              <section className="cb-q" aria-labelledby="q-sequence">
                <h2 id="q-sequence">Sequence</h2>
                <SequencePicker targets={targets} where={seqWhere} target={seqTarget} maw="22rem"
                                emptyLabel={sequenceTargets.isLoading ? "Loading the campaigns" : "Not in a sequence"}
                                description="Optional. It is called after the campaign ahead of it is done."
                                onWhere={(v) => placeAt(v, seqTarget)} onTarget={(v) => placeAt(seqWhere, v)} />
                {sequenceTargets.isError ? <p className="refusal">{(sequenceTargets.error as Error).message}</p> : null}
              </section>
            ) : null}

            {!editing ? (
              <section className="cb-q" aria-labelledby="q-owner">
                <h2 id="q-owner">Owner</h2>
                <NativeSelect
                  aria-labelledby="q-owner" maw="22rem" disabled={Boolean(placeTarget)}
                  value={placeTarget ? placeTarget.owner_id ?? "" : owner}
                  description={placeTarget
                    ? `The owner of the sequence ${placeTarget.label ?? placeTarget.name} is in.`
                    : "Optional. Assign it later from the campaign's page."}
                  error={accounts.isError ? (accounts.error as Error).message : undefined}
                  data={[{ value: "", label: accounts.isLoading ? "Loading the people who can sign in" : "No owner yet" },
                         ...(accounts.data ?? []).map((u) => ({ value: u.id ?? "", label: u.label ?? u.email ?? "" }))]}
                  onChange={(e) => setOwner(e.currentTarget.value)}
                />
              </section>
            ) : null}

            {!editing ? (
              <section className="cb-q" aria-labelledby="q-dates">
                <h2 id="q-dates">Dates</h2>
                <TextInput type="date" label="Estimated starting date" maw="16rem" min={today} value={callOn}
                           description={ahead
                             // The date moves when it reaches the front, which is
                             // when every campaign ahead is done (engine
                             // lake.leave_sequence_when_done, migration 312).
                             ? "Set to the next working day when the campaigns ahead of it are done."
                             : "The day the team expects to start calling."}
                           onChange={(e) => pickCallOn(e.currentTarget.value)} />
                {startsWithAhead ? (
                  <p className="hint">Enrichment starts when you save, because {aheadLabel} is being called.</p>
                ) : anyEnrichment ? (
                  <Radio.Group mt="md" label="Start enrichment" value={mode}
                               onChange={(v) => { setStartTouched(true); setMode(v as StartMode); }}>
                    <div className="cb-start">
                      <Radio value="now" label="Now" />
                      <Radio value="on_date" label="On a date" />
                      {mode === "on_date" ? (
                        <TextInput type="date" aria-label="Enrichment start date" maw="16rem" ml="xl" min={today}
                                   max={callOn || undefined} value={enrichOn}
                                   onChange={(e) => { setStartTouched(true); setEnrichOn(e.currentTarget.value); }} />
                      ) : null}
                      <Radio value="not_yet" label={ahead ? `When ${aheadLabel} starts calling` : "Not yet"} />
                    </div>
                  </Radio.Group>
                ) : null}
                {lead.isError ? <p className="refusal">{(lead.error as Error).message}</p> : null}
              </section>
            ) : null}

            <section className="cb-q" aria-labelledby="q-about">
              <h2 id="q-about">Description</h2>
              <Textarea aria-labelledby="q-about" description="Optional. Shown on the campaign's Criteria tab."
                        value={description} onChange={(e) => setDescription(e.currentTarget.value)} maw="34rem" autosize minRows={2} />
            </section>
          </div>

          <aside className="cb-answer" aria-label="What these criteria give">
            <p className="cb-count" aria-live="polite">
              {count.isError ? <span className="refusal">The count did not load. <Button kind="link" size="compact-sm" onClick={() => count.refetch()}>Try again</Button></span>
                : n === null ? <span className="muted"><Spinner />Counting</span>
                : <><strong>{uncounted.length ? "At most " : ""}{n.toLocaleString()}</strong> {n === 1 ? "property matches" : "properties match"}</>}
              {count.isFetching && n !== null ? <span className="muted cb-recount"><Spinner /></span> : null}
            </p>

            {counted.length ? (
              <div className="cb-without">
                <p className="field-label">Without each criterion</p>
                <table className="tight">
                  <tbody>
                    {counted.map((k) => {
                      const row = without.data?.find((w) => w.key === k);
                      const same = row?.n !== null && row?.n !== undefined && row.n === n;
                      return (
                        <tr key={k}>
                          <td>{criterion(k)?.label ?? k}</td>
                          <td className="num">
                            {!row ? <Spinner /> : same ? <span className="muted">same</span> : (row.n ?? 0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {uncounted.length ? (
              <p className="hint">
                Not in this count, applied when the list is made: {uncounted.map((k) => criterion(k)?.label ?? k).join(", ")}.{" "}
                {NOT_COUNTED[uncounted[0] as string] ?? ""}
              </p>
            ) : null}

            <NativeSelect
              label="Rank by" value={ranking}
              data={[
                ...RANKINGS.map((r) => ({ value: r.key, label: r.label })),
                ...(ranking === "engine" ? [{ value: "engine", label: "Set in the engine" }] : []),
              ]}
              onChange={(e) => setRanking(e.currentTarget.value as RankState)}
              description={ranking === "engine" ? "Saving keeps it." : rankingOf(ranking as RankKey).says}
            />

            <TextInput
              label="Name" mt="md" value={label}
              onChange={(e) => {
                setLabel(e.currentTarget.value);
                if (!nameEdited) setName(campaignSlug(e.currentTarget.value));
              }}
            />
            {/* The address is made from the name and shows only when the
                engine would refuse it, so the refusal can be fixed. */}
            {!editing && nameProblem && label.trim() ? (
              <TextInput label="Address" mt="xs" value={name} error={nameProblem}
                         onChange={(e) => { setNameEdited(true); setName(e.currentTarget.value); }} />
            ) : null}

            {editing ? (
              <Checkbox checked={updateOnSave} onChange={(e) => setUpdateOnSave(e.currentTarget.checked)}
                        label="Update the list when saved" className="cb-update" />
            ) : null}
            <div className="cb-buttons">
              {editing ? (
                <Button kind="orange" onClick={saveChanges} disabled={!canSave} loading={saving === "save"}>Save changes</Button>
              ) : (
                <Button kind="orange" onClick={startCreate} disabled={!canSave} loading={saving === "create"}>
                  {label.trim() ? "Save campaign" : "Name it to save"}
                </Button>
              )}
              <Button kind="link" onClick={() => navigate(source ? campaignPath(source) : "/campaigns")}>Cancel</Button>
            </div>
            {/* What pressing it does, so the button needs one word. */}
            {!editing ? <p className="cb-line" aria-live="polite">{line}</p> : null}
            {said ? <p className="refusal" role="alert">{said}</p> : null}
          </aside>
          {/* The phone's bar: the count and the button, at the bottom of the
              screen while the questions scroll. */}
          <div className="cb-bar" aria-hidden="true">
            <span>{n === null ? <Spinner /> : <><strong>{n.toLocaleString()}</strong> match</>}</span>
            {editing
              ? <Button kind="orange" tabIndex={-1} onClick={saveChanges} disabled={!canSave}>Save changes</Button>
              : <Button kind="orange" tabIndex={-1} onClick={startCreate} disabled={!canSave}>{label.trim() ? "Save campaign" : "Name it to save"}</Button>}
          </div>
          {confirm.dialog}
          <h2 className="cb-matching">The properties that match</h2>
        </div>
      }
    >
      <DataTable columns={columns} rows={list} rowKey={(p) => p.id ?? ""} params={params} />
    </ListShell>
  );
}
